import React from 'react';
import './TestimonialsAdmin.css';

const testimonialDomains = [
  {domain: 'expatelitesingles.com', name: 'Expat Elite Singles'},
  {domain: 'penzi-halisi.com', name: 'Penzi Halisi'},
  {domain: 'upendo-halisi.com', name: 'Upendo Halisi'},
];

function defaultDomain() {
  const hostname = typeof window === 'undefined' ? '' :
    window.location.hostname.toLowerCase().replace(/^(?:(?:staging|www)\.)+/, '');
  return testimonialDomains.some(item => item.domain === hostname) ? hostname : testimonialDomains[0].domain;
}

async function readCatalogue() {
  const response = await fetch('/api/testimonials?scope=admin', { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error('Could not load uploaded testimonials.');
  const data = await response.json();
  if (!Array.isArray(data.videos)) throw new Error('The uploaded testimonial list is unavailable.');
  return data.videos;
}

// Upload progress measures browser → app server only, not Bunny processing.
function sendUpload(data, onProgress, onSent) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/sirri_api/testimonials');
    // XHR includes same-origin cookies by default. Do not enable cross-origin credentials
    // or set Content-Type: the browser must supply the FormData multipart boundary.
    xhr.timeout = 10 * 60 * 1000;
    xhr.upload.onprogress = onProgress;
    xhr.upload.onload = onSent;
    xhr.onload = () => resolve({ok: xhr.status >= 200 && xhr.status < 300,
      status: xhr.status, text: xhr.responseText});
    xhr.onerror = () => reject(new Error('Network error during upload. Publication status is unknown.'));
    xhr.onabort = () => reject(new Error('Upload was aborted. Publication status is unknown.'));
    xhr.ontimeout = () => reject(new Error('Upload timed out. Publication status is unknown.'));
    xhr.send(data);
  });
}

function uploadedDate(value) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }) : 'Date unavailable';
}

export default function TestimonialsAdmin() {
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [videos, setVideos] = React.useState([]);
  // One synchronous lock covers uploads, removals and manual refreshes.
  const uploading = React.useRef(false);
  const [mediaType, setMediaType] = React.useState('video');
  const [removing, setRemoving] = React.useState(null);
  const [uncertain, setUncertain] = React.useState(false);
  const confirmation = React.useRef(null);
  const needsReload = React.useRef(false);
  const revision = React.useRef(0);
  const removeTrigger = React.useRef(null);
  const reloadButton = React.useRef(null);
  const [uploadState, setUploadState] = React.useState(null);
  const [statusKind, setStatusKind] = React.useState('info');
  const [selectedDomain, setSelectedDomain] = React.useState(defaultDomain);
  const isPicture = mediaType === 'image';
  const blocked = busy || Boolean(removing) || uncertain;

  React.useEffect(() => {
    let active = true;
    const initialRevision = revision.current;
    readCatalogue().then(items => {
      if (active && revision.current === initialRevision) setVideos(items);
    }).catch(error => {
      if (active && revision.current === initialRevision) setMessage(error.message);
    });
    return () => { active = false; };
  }, []);

  // Restore focus after React has re-enabled controls or removed the deleted row.
  React.useEffect(() => {
    if (!removing && removeTrigger.current) {
      const trigger = removeTrigger.current;
      const target = trigger.isConnected === false || needsReload.current ? reloadButton.current : trigger;
      if (target) target.focus();
      removeTrigger.current = null;
    }
  }, [removing]);

  function requireReload() { needsReload.current = true; setUncertain(true); }

  async function reload() {
    if (uploading.current || confirmation.current) return;
    uploading.current = true; revision.current += 1; setBusy(true);
    setUploadState(null); setStatusKind('info');
    try {
      setVideos(await readCatalogue());
      needsReload.current = false; setUncertain(false);
      setMessage('Uploaded testimonials reloaded. Review the list before trying again.');
      setStatusKind('success');
    } catch (error) { setStatusKind('error'); setMessage(`${error.message} Reload the list before retrying.`); }
    finally { uploading.current = false; setBusy(false); }
  }

  async function upload(event) {
    event.preventDefault();
    if (uploading.current || confirmation.current || needsReload.current) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    data.set('domain', selectedDomain);
    const file = data.get(mediaType);
    setStatusKind('error'); setUploadState(null);
    if (!file || !file.size) { setMessage(`Choose a ${isPicture ? 'picture' : 'video'} first.`); return; }
    if (file.type && !file.type.startsWith(`${mediaType}/`)) {
      setMessage(`Choose a ${isPicture ? 'picture' : 'video'} file.`); return;
    }
    uploading.current = true; revision.current += 1;
    setBusy(true); setStatusKind('info');
    setUploadState({phase: 'sending', percent: null});
    setMessage(`Uploading ${isPicture ? 'picture' : 'video'} to the server. Please keep this page open…`);
    let accepted = false;
    let receivedResponse = false;
    let invalidResponse = false;
    let waiting = false;
    const sent = () => {
      waiting = true;
      setUploadState({phase: 'waiting', percent: 100});
      setMessage(isPicture ? 'File sent to the app server. Waiting for server processing…' :
        'File sent to the app server. Waiting for server/Bunny processing…');
    };
    try {
      const response = await sendUpload(data, event => {
        if (waiting) return;
        const percent = event.lengthComputable && event.total > 0 ?
          Math.min(100, Math.floor(event.loaded / event.total * 100)) : null;
        if (percent === 100) { sent(); return; }
        setUploadState({phase: 'sending', percent});
        setMessage(`Uploading ${isPicture ? 'picture' : 'video'} to the app server${percent === null ? ' (size unknown)' : `: ${percent}%`}. Please keep this page open…`);
      }, sent);
      receivedResponse = true;
      accepted = response.ok;
      let result;
      try {
        result = JSON.parse(response.text);
        if (!result || typeof result !== 'object') throw new Error('Invalid response');
      } catch (_) {
        invalidResponse = true;
        throw new Error(`The server returned an unreadable response (HTTP ${response.status}). Upload publication could not be confirmed.`);
      }
      if (!response.ok) throw new Error(result.message || `Upload failed (HTTP ${response.status}). Please try again.`);
      setUploadState({phase: 'verifying', percent: null});
      setMessage('Server accepted the upload. Verifying publication in the saved list…');
      let listing;
      try { listing = await readCatalogue(); }
      catch (_) { throw new Error('Upload completed, but the saved list could not be refreshed. Reload this page before retrying.'); }
      const uploaded = result.testimonial || result.video;
      const mediaUrl = isPicture ? 'image' : 'embed';
      if (!uploaded?.id || !uploaded[mediaUrl] || uploaded.domain !== selectedDomain ||
          (isPicture && uploaded.type !== 'image') ||
          !listing.some(item => item.id === uploaded.id && item[mediaUrl] === uploaded[mediaUrl] &&
            item.domain === selectedDomain &&
            (isPicture ? item.type === 'image' : item.type !== 'image'))) {
        throw new Error('Upload completed, but publication could not be confirmed. Reload this page before retrying.');
      }
      setVideos(listing);
      form.reset(); setMediaType('video');
      setUploadState(null); setStatusKind('success');
      setMessage(isPicture ? `Picture testimonial added at the top of the homepage testimonials on ${selectedDomain}. The picture is stored on the server.` :
        `Testimonial added at the top of the homepage testimonials on ${selectedDomain}. Bunny may need a little time to process the video.`);
    } catch (error) {
      setUploadState(null); setStatusKind('error');
      if (accepted || !receivedResponse || invalidResponse) {
        requireReload();
        setMessage(`${error.message || 'Upload status is uncertain.'} Reload the list before retrying.`);
      } else setMessage(error.message || 'Upload failed.');
    } finally { uploading.current = false; setBusy(false); }
  }

  function askRemove(item, event) {
    if (uploading.current || confirmation.current || needsReload.current) return;
    removeTrigger.current = event.currentTarget;
    confirmation.current = item; setRemoving(item); setMessage('');
  }

  function closeConfirmation() {
    if (uploading.current) return;
    confirmation.current = null; setRemoving(null);
  }

  async function remove() {
    const item = confirmation.current;
    if (!item || uploading.current || needsReload.current) return;
    uploading.current = true; revision.current += 1; setBusy(true);
    setUploadState(null); setStatusKind('info');
    setMessage('Removing testimonial from the website…');
    try {
      const response = await fetch(`/api/sirri_api/testimonials/${encodeURIComponent(item.id)}`, {
        method: 'DELETE', credentials: 'same-origin',
      });
      const result = await response.json();
      if (!response.ok || result.responsecode !== 200 || result.deleted_id !== item.id) {
        throw new Error(result.message || 'Removal could not be confirmed.');
      }
      const listing = await readCatalogue();
      if (listing.some(saved => saved.id === item.id)) throw new Error('The testimonial is still in the published list.');
      setVideos(listing);
      setMessage('Testimonial removed from the website. Underlying media has not been deleted.');
      setStatusKind('success');
    } catch (error) {
      setStatusKind('error');
      requireReload();
      setMessage(`${error.message || 'Removal status is uncertain.'} Reload the list before retrying.`);
    } finally {
      uploading.current = false; setBusy(false); closeConfirmation();
    }
  }

  const pictures = videos.filter(item => item.type === 'image').length;
  return <section className="testimonials-admin" aria-labelledby="testimonials-heading">
    <header className="ta-header">
      <div><p className="ta-eyebrow">Website content</p><h1 id="testimonials-heading">Testimonials</h1>
        <p>Share real stories. Manage the pictures and videos published in your homepage gallery.</p></div>
      <a className="ta-link-button" href={`https://${selectedDomain}/#testimonials`} target="_blank" rel="noopener noreferrer">Open website gallery ↗</a>
    </header>
    <div className="ta-layout">
      <section className="ta-panel ta-upload" aria-labelledby="upload-heading">
        <p className="ta-eyebrow">Add a story</p><h2 id="upload-heading">Upload testimonial</h2>
        <p>New uploads appear first. Existing website testimonials remain below your uploads.</p>
        <form onSubmit={upload} aria-busy={busy}>
          <label htmlFor="testimonial-type">Testimonial type</label>
          <select id="testimonial-type" value={mediaType} disabled={blocked} onChange={event => {
            if (uploading.current || confirmation.current || needsReload.current) return;
            setMediaType(event.target.value); setMessage(''); setUploadState(null); setStatusKind('info');
          }}><option value="video">Video</option><option value="image">Picture</option></select>
          <label htmlFor="testimonial-domain">Website/domain</label>
          <select id="testimonial-domain" name="domain" value={selectedDomain} disabled={blocked} onChange={event => {
            if (uploading.current || confirmation.current || needsReload.current) return;
            setSelectedDomain(event.target.value); setMessage(''); setUploadState(null); setStatusKind('info');
          }}>{testimonialDomains.map(item => <option key={item.domain} value={item.domain}>{item.name} ({item.domain})</option>)}</select>
          <label htmlFor="testimonial-title">Title <span className="ta-muted">(optional)</span></label>
          <input id="testimonial-title" name="title" type="text" maxLength={200} placeholder="For example, A new chapter together" disabled={blocked} />
          <label htmlFor={`testimonial-${mediaType}`}>Testimonial {isPicture ? 'picture' : 'video'}</label>
          <input key={mediaType} id={`testimonial-${mediaType}`} name={mediaType} type="file" accept={isPicture ? 'image/jpeg,image/png' : 'video/*'} required disabled={blocked} aria-describedby="testimonial-file-help" />
          <p id="testimonial-file-help" className="ta-help">{isPicture ? 'JPEG or PNG, up to 10 MB and 20 megapixels. Stored on the server as optimized images.' : 'Videos are uploaded to Bunny. Processing may take a little time before playback is ready.'}</p>
          <button className="ta-primary" type="submit" disabled={blocked}>{busy && !removing ? 'Working…' : 'Upload testimonial'}</button>
        </form>
        <div className={`ta-status ta-status-${statusKind}${uncertain ? ' ta-status-warning' : ''}`} role={statusKind === 'error' ? 'alert' : 'status'} aria-live={statusKind === 'error' ? 'assertive' : 'polite'} aria-atomic="true">
          {message && <p>{message}</p>}
          {uploadState && <div className="ta-upload-progress">
            <progress max="100" value={uploadState.percent === null ? undefined : uploadState.percent} aria-label={uploadState.phase === 'sending' ? 'File transmission to app server' : uploadState.phase === 'waiting' ? 'File sent to app server' : 'Verifying publication'} />
            <span>{uploadState.phase === 'verifying' ? 'Checking saved record…' : uploadState.percent === null ? 'Transmission progress unavailable' : `${uploadState.percent}% sent to app server`}</span>
          </div>}
        </div>
      </section>
      <section className="ta-panel ta-library" aria-labelledby="library-heading">
        <div className="ta-library-header"><div><p className="ta-eyebrow">Published uploads</p><h2 id="library-heading">Your testimonial library</h2></div>
          <button ref={reloadButton} type="button" className="ta-secondary" onClick={reload} disabled={busy || Boolean(removing)}>Reload list</button></div>
        <div className="ta-counts" aria-label="Uploaded testimonial counts"><span><strong>{videos.length}</strong> total</span><span><strong>{videos.length - pictures}</strong> videos</span><span><strong>{pictures}</strong> pictures</span></div>
        <p className="ta-help">Newest first. Remove unpublishes an upload from the website; it does not delete the underlying Bunny video or server image.</p>

        {removing && <div className="ta-confirm" role="alertdialog" aria-labelledby="remove-heading" aria-describedby="remove-description" onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); closeConfirmation(); }
        }}>
          <h3 id="remove-heading">Remove this testimonial from the website?</h3>
          <p id="remove-description">“{removing.title || 'Untitled testimonial'}” will no longer appear in the uploaded gallery. The underlying media is kept, not deleted from Bunny or server storage.</p>
          <div className="ta-actions"><button type="button" autoFocus className="ta-secondary" onClick={closeConfirmation} disabled={busy}>Cancel</button>
            <button type="button" className="ta-danger" onClick={remove} disabled={busy}>{busy ? 'Removing…' : 'Remove from website'}</button></div>
        </div>}
        {!videos.length && <div className="ta-empty"><h3>No uploaded testimonials yet</h3><p>Add your first picture or video using the upload form. Built-in website testimonials are not managed here.</p></div>}
        <ol className="ta-list">{videos.map(item => <li className="ta-item" key={item.id || item.embed}>
          <a className="ta-preview" href={item.type === 'image' ? item.image : item.embed} target="_blank" rel="noopener noreferrer" aria-label={`View ${item.title || 'testimonial'}`}>
            {(item.type === 'image' ? item.image : item.thumb) ? <img loading="lazy" src={item.type === 'image' ? item.image : item.thumb} alt={item.title || (item.type === 'image' ? 'Picture testimonial' : 'Video testimonial thumbnail')} /> : <span className="ta-preview-placeholder">Video preview</span>}
          </a>
          <div className="ta-item-info"><span className={`ta-badge ${item.type === 'image' ? 'ta-picture' : ''}`}>{item.type === 'image' ? 'Picture' : 'Video'}</span>
            <h3>{item.title || 'Untitled testimonial'}</h3><p className="ta-help">{item.domain || 'All domains (legacy)'}</p>
            <p className="ta-help">Added {uploadedDate(item.created_at)}</p>
            <div className="ta-actions"><a href={item.type === 'image' ? item.image : item.embed} target="_blank" rel="noopener noreferrer">View ↗</a>
              <button type="button" className="ta-remove" disabled={blocked || !item.id} onClick={event => askRemove(item, event)} aria-label={`Remove ${item.title || 'testimonial'} from website`}>Remove</button></div>
          </div>
        </li>)}</ol>
      </section>
    </div>
  </section>;
}
